import { useState, useCallback, useRef, useEffect } from 'react'
import { useNodesState, useEdgesState, addEdge } from '@xyflow/react'
import Dagre from '@dagrejs/dagre'
import {
  ArrowLeft, Play, Square, Save, LayoutGrid,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  FolderOpen, FilePlus, X, Link, Trash2
} from 'lucide-react'
import EtlCanvas from './EtlCanvas'
import EtlNodePalette from './EtlNodePalette'
import EtlConfigPanel from './EtlConfigPanel'
import EtlExecutionLog from './EtlExecutionLog'
import ConnectionManagerModal from './ConnectionManagerModal'
import { ETL_NODE_REGISTRY } from '../../data/etlNodeRegistry'
import { driverIdForNodeType } from '../../data/etlDriverRegistry'
import { propagateSchemas } from '../../data/etlSchemaPropagation'
import { EtlPipelineExecutor, EtlEvents } from '../../data/etlPipelineExecutor'
import {
  listPipelines,
  getPipeline,
  savePipeline,
  updatePipeline,
} from '../../data/etlFileService'
import './EtlDesignerView.css'

// Auto-layout using Dagre (same pattern as workflow DesignerView)
const getLayoutedElements = (nodes, edges, direction = 'LR') => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: direction,
    nodesep: 80,
    ranksep: 120,
    marginx: 50,
    marginy: 50,
  })

  nodes.forEach((node) => {
    g.setNode(node.id, { width: 240, height: 90 })
  })

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target)
  })

  Dagre.layout(g)

  return {
    nodes: nodes.map((node) => {
      const pos = g.node(node.id)
      return {
        ...node,
        position: { x: pos.x - 120, y: pos.y - 45 },
      }
    }),
    edges,
  }
}

function EtlDesignerView({ onBack, onOpenSettings, onOpenHelp, userProfile }) {
  // Node/edge state via ReactFlow hooks
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Selection state
  const [selectedNodeId, setSelectedNodeId] = useState(null)

  // Pipeline name
  const [pipelineName, setPipelineName] = useState('Untitled Pipeline')
  const [isEditingName, setIsEditingName] = useState(false)
  const nameInputRef = useRef(null)

  // Panel collapse state
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true)

  // Execution log panel
  const [logPanelExpanded, setLogPanelExpanded] = useState(false)

  // Execution state
  const [executing, setExecuting] = useState(false)
  const [executionLogs, setExecutionLogs] = useState([])
  const [executionProgress, setExecutionProgress] = useState({})

  // Pipeline persistence
  const [pipelineId, setPipelineId] = useState(null)
  const [showOpenDialog, setShowOpenDialog] = useState(false)
  const [savedPipelines, setSavedPipelines] = useState([])
  const [pipelinesLoading, setPipelinesLoading] = useState(false)

  // Connection Manager modal
  const [connectionManagerOpen, setConnectionManagerOpen] = useState(false)
  const [connectionManagerNodeId, setConnectionManagerNodeId] = useState(null)
  const [connectionManagerDriverId, setConnectionManagerDriverId] = useState(null)
  const [connectionManagerConnectionId, setConnectionManagerConnectionId] = useState(null)

  // Ref for ReactFlow instance (used by layout to call fitView)
  const reactFlowRef = useRef(null)

  // Ref for pipeline executor (needed by handleStop)
  const executorRef = useRef(null)

  // Schema propagation — run whenever nodes config or edges change
  const propagationTimer = useRef(null)
  useEffect(() => {
    // Debounce propagation to avoid running on every keystroke
    if (propagationTimer.current) clearTimeout(propagationTimer.current)
    propagationTimer.current = setTimeout(() => {
      if (nodes.length === 0) return
      // Deep-copy nodes to avoid direct mutation triggering re-render loops
      const updated = propagateSchemas(
        nodes.map(n => ({ ...n, data: { ...n.data } })),
        edges
      )
      // Only update if schemas actually changed
      let changed = false
      for (let i = 0; i < updated.length; i++) {
        const prev = nodes[i]?.data
        const next = updated[i]?.data
        if (
          JSON.stringify(prev?.inputSchema) !== JSON.stringify(next?.inputSchema) ||
          JSON.stringify(prev?.outputSchema) !== JSON.stringify(next?.outputSchema)
        ) {
          changed = true
          break
        }
      }
      if (changed) {
        setNodes(updated)
      }
    }, 150)
    return () => { if (propagationTimer.current) clearTimeout(propagationTimer.current) }
  }, [nodes, edges]) // eslint-disable-line react-hooks/exhaustive-deps

  // Focus name input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  // Auto-open right panel when a node is selected
  useEffect(() => {
    if (selectedNodeId) {
      setRightPanelCollapsed(false)
    }
  }, [selectedNodeId])

  // ---------- Handlers ----------

  // Connect two nodes via edge
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: `e-${params.source}-${params.target}`,
        type: 'smoothstep',
        animated: false,
      }
      setEdges((eds) => addEdge(newEdge, eds))
    },
    [setEdges]
  )

  // Node selection
  const onNodeSelect = useCallback((nodeId) => {
    setSelectedNodeId(nodeId)
  }, [])

  // Add a new node (from palette drop)
  const onNodeAdd = useCallback(
    (newNode) => {
      setNodes((nds) => [...nds, newNode])
      setSelectedNodeId(newNode.id)
    },
    [setNodes]
  )

  // Update a config key on a node
  const onConfigChange = useCallback(
    (nodeId, key, value) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id !== nodeId) return node

          // Special keys for top-level data fields
          if (key === '_label') {
            return { ...node, data: { ...node.data, label: value } }
          }
          if (key === '_enabled') {
            return { ...node, data: { ...node.data, enabled: value } }
          }
          if (key === '_connectionProfileId') {
            return { ...node, data: { ...node.data, connectionProfileId: value } }
          }
          if (key === '_connectionId') {
            return { ...node, data: { ...node.data, connectionId: value } }
          }
          if (key === '_connectionSnapshot') {
            return { ...node, data: { ...node.data, connectionSnapshot: value } }
          }
          if (key === '_outputSchema') {
            return { ...node, data: { ...node.data, outputSchema: value } }
          }

          return {
            ...node,
            data: {
              ...node.data,
              config: { ...node.data.config, [key]: value },
            },
          }
        })
      )
    },
    [setNodes]
  )

  // Delete a node and its connected edges
  const onDeleteNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId)
      )
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null)
      }
    },
    [setNodes, setEdges, selectedNodeId]
  )

  // Apply dagre auto-layout and fit view
  const onLayout = useCallback(
    (direction) => {
      if (nodes.length === 0) return

      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nodes, edges, direction)

      setNodes(layoutedNodes)
      setEdges(layoutedEdges)

      // Fit view after layout settles
      setTimeout(() => {
        reactFlowRef.current?.fitView({ padding: 0.2 })
      }, 50)
    },
    [nodes, edges, setNodes, setEdges]
  )

  // Append a log entry
  const addLog = useCallback((message, level = 'info', source = 'system') => {
    const entry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      message,
      level,
      source,
    }
    setExecutionLogs((prev) => [...prev, entry])
  }, [])

  // Run pipeline — create executor, subscribe to events, execute
  const handleRun = useCallback(() => {
    if (nodes.length === 0) return

    setExecuting(true)
    setLogPanelExpanded(true)
    setExecutionProgress({})
    addLog('Pipeline execution started.', 'info', 'pipeline')
    addLog(
      `Executing ${nodes.length} node(s) in pipeline "${pipelineName}".`,
      'info',
      'pipeline'
    )

    const executor = new EtlPipelineExecutor(nodes, edges)
    executorRef.current = executor

    // Subscribe to events
    executor.on(EtlEvents.NODE_START, ({ nodeId, label }) => {
      addLog(`[${label}] Starting...`, 'info', 'node')
      setExecutionProgress(prev => ({
        ...prev,
        [nodeId]: { status: 'running', progress: 0 },
      }))
      // Update node visual status
      setNodes(nds => nds.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, status: 'running' } } : n
      ))
    })

    executor.on(EtlEvents.NODE_PROGRESS, ({ nodeId, label, progress, message }) => {
      addLog(`[${label}] ${message}`, 'info', 'node')
      setExecutionProgress(prev => ({
        ...prev,
        [nodeId]: { status: 'running', progress },
      }))
    })

    executor.on(EtlEvents.NODE_COMPLETE, ({ nodeId, label, result }) => {
      const summary = result?.type === 'sql_execution'
        ? `${result.succeeded}/${result.totalStatements} statements OK (${result.durationMs}ms)`
        : result?.type === 'sql'
        ? `${result.statementCount} statement(s) loaded`
        : 'Done'
      addLog(`[${label}] Complete: ${summary}`, 'info', 'node')

      // Log individual statement results for SQL execution
      if (result?.results) {
        for (const r of result.results) {
          if (r.status === 'ok') {
            addLog(`  Statement ${r.statement}: OK (${r.durationMs}ms)`, 'info', 'sql')
          } else if (r.status === 'error') {
            addLog(`  Statement ${r.statement}: ERROR — ${r.error}`, 'error', 'sql')
          } else if (r.status === 'skipped') {
            addLog(`  Statement ${r.statement}: SKIPPED`, 'warn', 'sql')
          }
        }
      }

      setExecutionProgress(prev => ({
        ...prev,
        [nodeId]: { status: 'complete', progress: 100, result },
      }))
      setNodes(nds => nds.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, status: 'complete', executionResult: result } } : n
      ))
    })

    executor.on(EtlEvents.NODE_ERROR, ({ nodeId, label, error }) => {
      addLog(`[${label}] ERROR: ${error}`, 'error', 'node')
      setExecutionProgress(prev => ({
        ...prev,
        [nodeId]: { status: 'error', error },
      }))
      setNodes(nds => nds.map(n =>
        n.id === nodeId ? { ...n, data: { ...n.data, status: 'error' } } : n
      ))
    })

    executor.on(EtlEvents.NODE_SKIPPED, ({ nodeId, label }) => {
      addLog(`[${label}] Skipped (disabled)`, 'warn', 'node')
    })

    executor.on(EtlEvents.PIPELINE_COMPLETE, ({ durationMs, nodesExecuted }) => {
      addLog(`Pipeline complete: ${nodesExecuted} node(s) executed in ${durationMs}ms.`, 'info', 'pipeline')
      setExecuting(false)
      executorRef.current = null
    })

    executor.on(EtlEvents.PIPELINE_ERROR, ({ error, durationMs }) => {
      addLog(`Pipeline finished with error: ${error} (${durationMs || 0}ms)`, 'error', 'pipeline')
      setExecuting(false)
      executorRef.current = null
    })

    // Fire and forget — events drive state updates
    executor.execute()
  }, [nodes, edges, pipelineName, addLog, setNodes])

  // Stop pipeline — abort the running executor
  const handleStop = useCallback(() => {
    if (executorRef.current) {
      executorRef.current.abort()
      executorRef.current = null
    }
    setExecuting(false)
    addLog('Pipeline execution stopped by user.', 'warn', 'pipeline')
    // Clear running status from all nodes
    setNodes(nds => nds.map(n =>
      n.data?.status === 'running'
        ? { ...n, data: { ...n.data, status: undefined } }
        : n
    ))
  }, [addLog, setNodes])

  // Save pipeline to file server
  const handleSave = useCallback(async () => {
    try {
      const payload = {
        name: pipelineName,
        description: '',
        nodes,
        edges,
      }
      let result
      if (pipelineId) {
        result = await updatePipeline(pipelineId, payload)
        addLog(`Pipeline "${pipelineName}" updated (v${result.version}).`, 'info', 'system')
      } else {
        result = await savePipeline(payload)
        setPipelineId(result.id)
        addLog(`Pipeline "${pipelineName}" saved as ${result.id}.`, 'info', 'system')
      }
    } catch (err) {
      addLog(`Save failed: ${err.message}`, 'error', 'system')
    }
  }, [pipelineName, nodes, edges, pipelineId, addLog])

  // Open pipeline dialog
  const handleOpenDialog = useCallback(async () => {
    setPipelinesLoading(true)
    try {
      const list = await listPipelines()
      setSavedPipelines(list)
    } catch {
      setSavedPipelines([])
    } finally {
      setPipelinesLoading(false)
      setShowOpenDialog(true)
    }
  }, [])

  // Load a pipeline from file server
  const handleLoadPipeline = useCallback(async (id) => {
    try {
      const pipeline = await getPipeline(id)
      setNodes(pipeline.nodes || [])
      setEdges(pipeline.edges || [])
      setPipelineName(pipeline.name || 'Untitled Pipeline')
      setPipelineId(pipeline.id)
      setSelectedNodeId(null)
      setShowOpenDialog(false)
      addLog(`Loaded pipeline "${pipeline.name}" (v${pipeline.version}).`, 'info', 'system')
      setTimeout(() => {
        reactFlowRef.current?.fitView({ padding: 0.2 })
      }, 100)
    } catch (err) {
      addLog(`Load failed: ${err.message}`, 'error', 'system')
    }
  }, [setNodes, setEdges, addLog])

  // New pipeline — clear canvas
  const handleNewPipeline = useCallback(() => {
    setNodes([])
    setEdges([])
    setPipelineName('Untitled Pipeline')
    setPipelineId(null)
    setSelectedNodeId(null)
    setExecutionLogs([])
    setExecutionProgress({})
    setShowOpenDialog(false)
  }, [setNodes, setEdges])

  // Pipeline name editing handlers
  const handleNameBlur = useCallback(() => {
    if (!pipelineName.trim()) {
      setPipelineName('Untitled Pipeline')
    }
    setIsEditingName(false)
  }, [pipelineName])

  const handleNameKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleNameBlur()
      } else if (e.key === 'Escape') {
        setIsEditingName(false)
      }
    },
    [handleNameBlur]
  )

  // Open Connection Manager from a node
  const handleOpenConnectionManager = useCallback((node) => {
    const nodeType = node?.data?.nodeType || node?.data?.type
    const config = node?.data?.config || {}
    setConnectionManagerNodeId(node?.id || null)
    setConnectionManagerDriverId(driverIdForNodeType(nodeType, config))
    setConnectionManagerConnectionId(node?.data?.connectionId || null)
    setConnectionManagerOpen(true)
  }, [])

  // Handle connection applied from modal
  const handleConnectionApply = useCallback((connectionProfile) => {
    if (!connectionManagerNodeId) return
    // Apply connection to the node
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== connectionManagerNodeId) return node
        return {
          ...node,
          data: {
            ...node.data,
            connectionId: connectionProfile.id,
            connectionSnapshot: {
              name: connectionProfile.name,
              driverId: connectionProfile.driverId,
              host: connectionProfile.connectionFields?.host || '',
              port: connectionProfile.connectionFields?.port || '',
              database: connectionProfile.connectionFields?.database || '',
              schema: connectionProfile.connectionFields?.schema || '',
            },
            // Also write connection fields into config for backward compat
            config: {
              ...node.data.config,
              connection_type: connectionProfile.driverId,
              ...connectionProfile.connectionFields,
            },
          },
        }
      })
    )
    addLog(
      `Connection "${connectionProfile.name}" applied to node.`,
      'info',
      'connection'
    )
  }, [connectionManagerNodeId, setNodes, addLog])

  // Store ReactFlow instance ref for fitView
  const onReactFlowInit = useCallback((instance) => {
    reactFlowRef.current = instance
  }, [])

  return (
    <div className="etl-designer-view">
      {/* Toolbar */}
      <div className="etl-toolbar">
        <button className="etl-toolbar-btn" onClick={onBack} title="Back">
          <ArrowLeft size={16} />
        </button>
        <div className="etl-toolbar-divider" />

        <span className="etl-toolbar-title">RecodeIT Data Studio</span>
        <div className="etl-toolbar-divider" />

        {/* Pipeline name - click to edit */}
        {isEditingName ? (
          <input
            ref={nameInputRef}
            className="etl-pipeline-name-input"
            value={pipelineName}
            onChange={(e) => setPipelineName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            autoFocus
          />
        ) : (
          <span
            className="etl-pipeline-name"
            onClick={() => setIsEditingName(true)}
            title="Click to rename pipeline"
          >
            {pipelineName}
          </span>
        )}

        <div className="etl-toolbar-spacer" />

        {/* Layout buttons */}
        <button
          className="etl-toolbar-btn"
          onClick={() => onLayout('LR')}
          title="Horizontal layout"
          disabled={nodes.length === 0}
        >
          <LayoutGrid size={16} />
        </button>
        <button
          className="etl-toolbar-btn"
          onClick={() => onLayout('TB')}
          title="Vertical layout"
          disabled={nodes.length === 0}
        >
          <LayoutGrid size={16} style={{ transform: 'rotate(90deg)' }} />
        </button>

        <div className="etl-toolbar-divider" />

        {/* Run / Stop */}
        {executing ? (
          <button
            className="etl-toolbar-btn etl-btn-danger"
            onClick={handleStop}
          >
            <Square size={16} /> Stop
          </button>
        ) : (
          <button
            className="etl-toolbar-btn etl-btn-primary"
            onClick={handleRun}
            disabled={nodes.length === 0}
          >
            <Play size={16} /> Run
          </button>
        )}

        {/* Save */}
        <button className="etl-toolbar-btn" onClick={handleSave}>
          <Save size={16} /> Save
        </button>

        {/* Open */}
        <button className="etl-toolbar-btn" onClick={handleOpenDialog}>
          <FolderOpen size={16} /> Open
        </button>

        {/* New */}
        <button className="etl-toolbar-btn" onClick={handleNewPipeline}>
          <FilePlus size={16} /> New
        </button>

        {/* Clear Canvas */}
        <button
          className="etl-toolbar-btn etl-btn-danger"
          onClick={() => {
            setNodes([])
            setEdges([])
            setSelectedNodeId(null)
            setExecutionProgress({})
            addLog('Canvas cleared.', 'info', 'system')
          }}
          disabled={nodes.length === 0 || executing}
          title="Clear all nodes from canvas"
        >
          <Trash2 size={16} /> Clear
        </button>

        <div className="etl-toolbar-divider" />

        {/* Connections */}
        <button
          className="etl-toolbar-btn"
          onClick={() => {
            setConnectionManagerNodeId(null)
            setConnectionManagerDriverId(null)
            setConnectionManagerConnectionId(null)
            setConnectionManagerOpen(true)
          }}
          title="Manage connections"
        >
          <Link size={16} /> Connections
        </button>
      </div>

      {/* Main content area */}
      <div className="etl-content">
        {/* Left Panel — Node Palette */}
        <div className={`etl-left-panel ${leftPanelCollapsed ? 'collapsed' : ''}`}>
          {!leftPanelCollapsed && (
            <div className="etl-left-panel-content">
              <EtlNodePalette disabled={executing} />
            </div>
          )}
          <button
            className="etl-panel-toggle etl-panel-toggle-left"
            onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            title={leftPanelCollapsed ? 'Show palette' : 'Hide palette'}
          >
            {leftPanelCollapsed ? (
              <ChevronRight size={14} />
            ) : (
              <ChevronLeft size={14} />
            )}
          </button>
        </div>

        {/* Center Canvas */}
        <div className="etl-canvas-container">
          <EtlCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeSelect={onNodeSelect}
            onNodeAdd={onNodeAdd}
            selectedNodeId={selectedNodeId}
            isExecuting={executing}
            onInit={onReactFlowInit}
          />
        </div>

        {/* Right Panel — Config */}
        <div className={`etl-right-panel ${rightPanelCollapsed ? 'collapsed' : ''}`}>
          <button
            className="etl-panel-toggle etl-panel-toggle-right"
            onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
            title={rightPanelCollapsed ? 'Show config panel' : 'Hide config panel'}
          >
            {rightPanelCollapsed ? (
              <ChevronLeft size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </button>
          {!rightPanelCollapsed && (
            <div className="etl-right-panel-content">
              {selectedNodeId ? (
                <EtlConfigPanel
                  selectedNode={nodes.find((n) => n.id === selectedNodeId)}
                  onConfigChange={onConfigChange}
                  onDeleteNode={onDeleteNode}
                  executing={executing}
                  executionProgress={executionProgress}
                  onOpenConnectionManager={handleOpenConnectionManager}
                />
              ) : (
                <div className="etl-config-panel">
                  <div className="etl-config-empty">
                    <span>Select a node to configure</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Log Panel */}
      <div className={`etl-log-panel ${logPanelExpanded ? 'expanded' : ''}`}>
        <div
          className="etl-log-header"
          onClick={() => setLogPanelExpanded(!logPanelExpanded)}
        >
          <span>
            Execution Log{' '}
            {executionLogs.length > 0 ? `(${executionLogs.length})` : ''}
          </span>
          {logPanelExpanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronUp size={14} />
          )}
        </div>
        {logPanelExpanded && (
          <EtlExecutionLog
            logs={executionLogs}
            onClear={() => setExecutionLogs([])}
          />
        )}
      </div>

      {/* Connection Manager Modal */}
      <ConnectionManagerModal
        isOpen={connectionManagerOpen}
        onClose={() => setConnectionManagerOpen(false)}
        initialConnectionId={connectionManagerConnectionId}
        initialDriverId={connectionManagerDriverId}
        onApply={handleConnectionApply}
      />

      {/* Open Pipeline Dialog */}
      {showOpenDialog && (
        <div className="etl-dialog-overlay" onClick={() => setShowOpenDialog(false)}>
          <div className="etl-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="etl-dialog-header">
              <h3>Open Pipeline</h3>
              <button
                className="etl-dialog-close"
                onClick={() => setShowOpenDialog(false)}
              >
                <X size={16} />
              </button>
            </div>
            <div className="etl-dialog-body">
              {pipelinesLoading ? (
                <div className="etl-dialog-empty">Loading...</div>
              ) : savedPipelines.length === 0 ? (
                <div className="etl-dialog-empty">
                  No saved pipelines found. Save a pipeline first.
                </div>
              ) : (
                <div className="etl-pipeline-list">
                  {savedPipelines.map((p) => (
                    <div
                      key={p.id}
                      className={`etl-pipeline-list-item ${
                        pipelineId === p.id ? 'active' : ''
                      }`}
                      onClick={() => handleLoadPipeline(p.id)}
                    >
                      <div className="etl-pipeline-list-name">{p.name}</div>
                      <div className="etl-pipeline-list-meta">
                        {p.nodeCount} nodes, {p.edgeCount} edges
                        {p.version > 1 ? ` (v${p.version})` : ''}
                      </div>
                      <div className="etl-pipeline-list-date">
                        {new Date(p.modified).toLocaleDateString()}{' '}
                        {new Date(p.modified).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EtlDesignerView
